import React from 'react'
import { Pill } from '../../../ui'

/**
 * Shared member pill used by tasks (assignee chip) and agenda (author pill).
 *
 * Renders a 16px initials avatar circle next to a short label. The label
 * collapses to "Me" when `userId === currentUserId`, otherwise to the
 * first name of `displayName`. The full name is exposed via `title=` on
 * hover.
 *
 * Props:
 *   userId          — who the pill represents (may be null/unknown)
 *   displayName     — their full name (may be null)
 *   currentUserId   — viewer; used to swap the label to "Me"
 *   onClick         — optional; when set, renders as a <button>
 *   disabled        — interactions disabled (snapshot or locked surfaces)
 *   variant         — 'task' | 'agenda'; controls minor styling
 *   trailing        — optional ReactNode rendered after the label
 *                     (e.g. an orphan badge)
 *   ariaLabelPrefix — 'Assignee' | 'Author' | …
 */
export function MemberPill({
  userId,
  displayName,
  currentUserId,
  onClick,
  disabled = false,
  variant = 'task',
  trailing = null,
  ariaLabelPrefix = 'Member',
}) {
  const isMe = !!userId && !!currentUserId && userId === currentUserId
  const fullName = displayName || (isMe ? 'You' : 'Unknown')
  const label = isMe ? 'Me' : firstName(displayName)
  const interactive = !!onClick && !disabled

  const ariaLabel = `${ariaLabelPrefix}: ${fullName}.${interactive ? ' Click to swap.' : ''}`
  // Asymmetric padding leaves room for the avatar circle on the left.
  const pillStyle = {
    padding: '1px 6px 1px 1px',
    fontSize: variant === 'agenda' ? 9 : 10,
    flexShrink: 0,
  }

  const inner = (
    <>
      <span aria-hidden="true" style={styles.avatar}>
        {initials(isMe ? 'Me' : displayName)}
      </span>
      <span style={styles.label}>{label || (isMe ? 'Me' : 'Unknown')}</span>
    </>
  )

  const body = interactive ? (
    <Pill
      as="button"
      type="button"
      tone="neutral"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={fullName}
      style={pillStyle}
    >
      {inner}
    </Pill>
  ) : (
    <Pill
      tone="neutral"
      title={fullName}
      aria-label={ariaLabel}
      style={pillStyle}
    >
      {inner}
    </Pill>
  )

  return (
    <span style={styles.wrapper}>
      {body}
      {trailing}
    </span>
  )
}

function initials(name) {
  if (!name) return '?'
  const trimmed = name.trim()
  if (!trimmed) return '?'
  const words = trimmed.split(/\s+/).slice(0, 2)
  const out = words.map((w) => w.charAt(0).toUpperCase()).join('')
  return out || '?'
}

function firstName(name) {
  if (!name) return ''
  return name.trim().split(/\s+/)[0]
}

const styles = {
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  avatar: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#d8d4cb',
    color: '#3a3a37',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 8,
    fontWeight: 600,
    flexShrink: 0,
  },
  label: {
    lineHeight: 1,
  },
}
